import React, { useEffect, useState } from 'react';
import { Inbox, Mail } from 'lucide-react';
import { getAccessRequests, updateAccessRequest } from '../api';

// Settings → Access Requests: what visitors sent from the landing page's
// Request access form, newest first. Admins mark each one contacted or
// archived; replies go out from the admin's own mail.

const FILTERS = [
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'archived', label: 'Archived' },
  { key: '', label: 'All' },
];

const TEAM_LABELS = { '1-5': '1 to 5', '6-20': '6 to 20', '21-50': '21 to 50', '51+': 'More than 50' };

const when = (iso) => new Date(String(iso).replace(' ', 'T') + 'Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

export default function AccessRequestsPanel() {
  const [filter, setFilter] = useState('new');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    getAccessRequests(filter ? { status: filter } : {})
      .then(res => { if (!cancelled) setRows(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load access requests'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filter]);

  const mark = async (id, status) => {
    try {
      await updateAccessRequest(id, status);
      setRows(list => (filter ? list.filter(r => r.id !== id) : list.map(r => (r.id === id ? { ...r, status } : r))));
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update that request');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Access Requests</h3>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>
          Sent from the Request access form on the public landing page.
        </p>
      </div>
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <div role="tablist" aria-label="Filter requests" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {FILTERS.map(f => (
            <button
              key={f.key || 'all'}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={`btn ${filter === f.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
      </div>
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">When</th>
              <th scope="col">Name</th>
              <th scope="col">Company</th>
              <th scope="col">Team</th>
              <th scope="col">Note</th>
              <th scope="col">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="6"><div className="loading-container"><div className="spinner"></div></div></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan="6"><div className="empty-state"><Inbox size={48} className="empty-state-icon" /><h3 className="empty-state-title">No requests here</h3></div></td></tr>
            ) : (
              rows.map(r => (
                <tr key={r.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>{when(r.created_at)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <a href={`mailto:${r.email}`} style={{ fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <Mail size={12} aria-hidden="true" /> {r.email}
                    </a>
                  </td>
                  <td>
                    <div>{r.company}</div>
                    {r.role && <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>{r.role}</div>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{TEAM_LABELS[r.team_size] || <span style={{ color: 'var(--text-muted)' }}>Not given</span>}</td>
                  <td style={{ minWidth: '220px', maxWidth: '320px', whiteSpace: 'pre-wrap' }}>{r.message || <span style={{ color: 'var(--text-muted)' }}>None</span>}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px' }}>
                      <span className={`badge ${r.status === 'new' ? 'badge-primary' : r.status === 'contacted' ? 'badge-success' : ''}`}>{r.status[0].toUpperCase() + r.status.slice(1)}</span>
                      {r.status !== 'contacted' && (
                        <button type="button" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }} onClick={() => mark(r.id, 'contacted')}>Mark contacted</button>
                      )}
                      {r.status !== 'archived' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => mark(r.id, 'archived')}>Archive</button>
                      )}
                      {r.status === 'archived' && (
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => mark(r.id, 'new')}>Restore</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
